import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import IncentiveCatalogCategories from "./../../components/IncentiveCatalogCategories/IncentiveCatalogCategories";
import IncentivePreferences from "../../client_customizations/components/IncentivesPreferences/IncentivesPreferences";
import { FormattedMessage, useIntl } from "react-intl";
import UserPrefsContext from "../../context/UserPrefs/UserPrefsContext";
import "./Incentives.scss";
import { ValidateCAPostCode, ValidateFiveDigitCode } from "../../utils/Helpers/PostalCodeValidator";
import { FormatAsDollars, parseDollarAmount } from "../../utils/Helpers/Format";
import {
     categorizeIncentiveForHero,
     isEligibleIncentive,
     isExcludedPassengerEvIncentive,
     isStateLikeIncentive,
     isUberGoElectricIncentive
} from "../../utils/Helpers/IncentiveCategories";
import fetchVehicleIncentivesWithHandle from "../../services/fetchVehicleIncentivesWithHandle";
import ZipChip from "../../components/ZipChip/ZipChip";

const INCENTIVES_PAGE_VEHICLE_HANDLE = "Tesla_Model_3_Standard_RWD_BEV_2026";
const INCENTIVES_PAGE_USED_VEHICLE_HANDLE = "Tesla_Model_3_Long_Range_AWD_BEV_2021";
const UBER_GO_ELECTRIC_SWITCHING_BONUS = 4000;

const CARD_CTA_FALLBACK = "https://www.uber.com/us/en/drive/services/electric/zero-emissions-incentive/";

const HARD_CODED_TOP_INCENTIVE = {
     id: "uber-zero-emissions-incentive-featured",
     name: "Go Electric Incentive",
     grantor: "Uber",
     type: "Driver bonus",
     max_amount: 4000,
     lead_description: "Get $4,000 for switching to an EV through Uber's Go Electric incentive.",
     description: "This bonus stacks on top of every federal, state, and local incentive you qualify for.",
     details_url: "https://www.uber.com/us/en/drive/services/electric/zero-emissions-incentive/"
};

const computeHeroTotal = (incentives) => {
     const list = Array.isArray(incentives) ? incentives : [];

     let newVehicleTotal = 0;
     let usedVehicleTotal = 0;

     const familyPatterns = [
          {
               key: "clean-cars-replace",
               regex: /clean cars (for all|4 all)|driving clean assistance|replace your ride/i
          }
     ];

     const familyBestAmounts = new Map();
     const skippedIndexes = new Set();

     list.forEach((incentive) => {
          if (!isEligibleIncentive(incentive)) return;
          if (isExcludedPassengerEvIncentive(incentive)) return;

          const amount = getStackableHeroAmount(incentive);
          if (amount <= 0) return;

          const text = `${incentive?.name || ""} ${incentive?.description || ""}`;

          familyPatterns.forEach((family) => {
               if (!family.regex.test(text)) return;

               const currentBest = familyBestAmounts.get(family.key);

               if (currentBest === undefined || amount > currentBest) {
                    familyBestAmounts.set(family.key, amount);
               }
          });
     });

     const claimedFamilies = new Set();

     list.forEach((incentive, index) => {
          if (!isEligibleIncentive(incentive)) return;
          if (isExcludedPassengerEvIncentive(incentive)) return;

          const amount = getStackableHeroAmount(incentive);
          if (amount <= 0) return;

          const text = `${incentive?.name || ""} ${incentive?.description || ""}`;

          familyPatterns.forEach((family) => {
               if (!family.regex.test(text)) return;

               if (claimedFamilies.has(family.key) || amount !== familyBestAmounts.get(family.key)) {
                    skippedIndexes.add(index);
               } else {
                    claimedFamilies.add(family.key);
               }
          });
     });

     const coversBothRegex =
          /clean cars (for all|4 all)|driving clean assistance|replace your ride|vehicle retirement/i;

     list.forEach((incentive, index) => {
          if (skippedIndexes.has(index)) return;
          if (!isEligibleIncentive(incentive)) return;
          if (isExcludedPassengerEvIncentive(incentive)) return;

          if (isUberGoElectricIncentive(incentive)) return;

          const type = String(incentive?.type || "").toLowerCase();

          if (type.includes("financ") || type.includes("loan")) return;

          const amount = getStackableHeroAmount(incentive);

          if (amount <= 0) return;

          const categories = categorizeIncentiveForHero(incentive);
          const text = `${incentive?.name || ""} ${incentive?.description || ""}`;
          const coversBoth = coversBothRegex.test(text);

          const supportFor = String(incentive?.support_for || incentive?.supportFor || "").toLowerCase();
          const isVehiclePurchaseSupport = supportFor === "vehicle purchase" || supportFor === "used vehicle purchase";
          const isVehicleRetirementType = type.includes("vehicle retirement");
          const isReplacementPurchaseProgram = coversBoth;

          if (isVehicleRetirementType && !isVehiclePurchaseSupport && !isReplacementPurchaseProgram) return;

          const supportSubtypes = Array.isArray(incentive?.support_subtype)
               ? incentive.support_subtype.join(" ").toLowerCase()
               : String(incentive?.support_subtype || "").toLowerCase();

          const focus = Array.isArray(incentive?.incentive_focus)
               ? incentive.incentive_focus.join(" ").toLowerCase()
               : String(incentive?.incentive_focus || "").toLowerCase();

          const isCharging =
               categories.includes("charging") ||
               supportFor.includes("charging") ||
               supportFor.includes("electricity") ||
               supportSubtypes.includes("charger") ||
               focus.includes("charger");

          if (isCharging) return;

          const explicitlyVehiclePurchase = supportFor === "vehicle purchase";

          const isNew =
               categories.includes("new") ||
               coversBoth ||
               (explicitlyVehiclePurchase && incentive?.applicable_to_new_vehicles === true);

          const isUsed = categories.includes("used") || coversBoth || supportFor === "used vehicle purchase";

          if (isCharging && !isNew && !isUsed) return;

          if (isNew && !isUsed) {
               newVehicleTotal += amount;
          } else if (isUsed && !isNew) {
               usedVehicleTotal += amount;
          } else if (isNew && isUsed) {
               newVehicleTotal += amount;
               usedVehicleTotal += amount;
          }
     });

     return Math.max(newVehicleTotal, usedVehicleTotal) + UBER_GO_ELECTRIC_SWITCHING_BONUS;
};

const fetchPageIncentivesByZip = async (postcode) => {
     const baseParams = {
          postcode: String(postcode).replace(" ", ""),
          household_size: 1,
          household_income: 10000,
          tax_filing_type: "single",
          turn_in_clunker: false,
          include_used_vehicles: true
     };

     const [newVehicleIncentives, usedVehicleIncentives] = await Promise.all([
          fetchVehicleIncentivesWithHandle({
               ...baseParams,
               vehicle_handle: INCENTIVES_PAGE_VEHICLE_HANDLE
          }).catch(() => []),

          fetchVehicleIncentivesWithHandle({
               ...baseParams,
               vehicle_handle: INCENTIVES_PAGE_USED_VEHICLE_HANDLE
          }).catch(() => [])
     ]);

     const seen = new Set();
     const mergedIncentives = [];

     [...(newVehicleIncentives || []), ...(usedVehicleIncentives || [])].forEach((incentive) => {
          const key = String(incentive?.name || "")
               .trim()
               .toLowerCase();

          if (!key || seen.has(key)) return;

          seen.add(key);
          mergedIncentives.push(incentive);
     });

     return mergedIncentives.filter((incentive) => !isExcludedPassengerEvIncentive(incentive));
};

const getLargestAmountFromText = (value) => {
     const text = String(value || "");
     const amountMatches = text.match(/\d[\d,]*(?:\.\d+)?/g);

     if (!amountMatches?.length) return 0;

     return Math.max(...amountMatches.map((amount) => Number(amount.replace(/,/g, ""))));
};

const getHeroIncentiveAmount = (incentive) => {
     const maxAmount = Math.max(
          parseDollarAmount(incentive?.max_amount),
          getLargestAmountFromText(incentive?.max_amount)
     );

     const typicalAmount = Math.max(
          parseDollarAmount(incentive?.typical_amount),
          getLargestAmountFromText(incentive?.typical_amount)
     );

     return Math.max(maxAmount, typicalAmount, 0);
};

const getStackableHeroAmount = (incentive) => {
     const heroAmount = getHeroIncentiveAmount(incentive);

     if (heroAmount > 0) {
          return heroAmount;
     }

     const parsedPurchaseAmount = Number(incentive?.evaluation?.amount_in_purchase);

     if (!isNaN(parsedPurchaseAmount) && parsedPurchaseAmount > 0) {
          return parsedPurchaseAmount;
     }

     return 0;
};

const parseIncentiveAmount = (incentive) => {
     const amount = getHeroIncentiveAmount(incentive);

     if (amount > 0) {
          return amount;
     }

     const parsedPurchaseAmount = Number(incentive?.evaluation?.amount_in_purchase);

     if (!isNaN(parsedPurchaseAmount) && parsedPurchaseAmount > 0) {
          return parsedPurchaseAmount;
     }

     return 0;
};

const getTypeRank = (type) => {
     const normalizedType = String(type || "").toLowerCase();
     if (normalizedType.includes("rebate") || normalizedType.includes("grant")) return 0;
     if (normalizedType.includes("tax")) return 1;
     if (normalizedType.includes("discount") || normalizedType.includes("toll") || normalizedType.includes("financ"))
          return 2;
     if (normalizedType.includes("rate") || normalizedType.includes("electric")) return 3;
     return 4;
};

const getPillLabel = (type) => {
     const normalizedType = String(type || "").toLowerCase();
     if (normalizedType.includes("trip")) return "Per-trip bonus";
     if (normalizedType.includes("driver") || normalizedType.includes("bonus")) return "Driver bonus";
     if (normalizedType.includes("rebate") || normalizedType.includes("grant")) return "Cash back";
     if (normalizedType.includes("tax")) return "Tax savings";
     if (normalizedType.includes("toll")) return "Toll perk";
     if (normalizedType.includes("financ") || normalizedType.includes("loan")) return "Financing";
     if (normalizedType.includes("discount")) return "Discount";
     if (normalizedType.includes("rate") || normalizedType.includes("electric")) return "Utility plan";
     if (normalizedType.includes("bill")) return "Bill credit";
     return type || "Program";
};

const PILL_LABEL_IDS = {
     "Driver bonus": "incentives.pill.driverBonus",
     "Per-trip bonus": "incentives.pill.perTripBonus",
     "Cash back": "incentives.pill.cashBack",
     "Tax savings": "incentives.pill.taxSavings",
     "Toll perk": "incentives.pill.tollPerk",
     Financing: "incentives.pill.financing",
     Discount: "incentives.pill.discount",
     "Utility plan": "incentives.pill.utilityPlan",
     "Bill credit": "incentives.pill.billCredit"
};

const getPillIcon = (label) => {
     const props = {
          width: 14,
          height: 14,
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: 2.5,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          "aria-hidden": true
     };

     switch (label) {
          case "Driver bonus":
          case "Per-trip bonus":
          case "Cash back":
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="9" />
                         <path d="M12 7v10M9 9.5c0-1.1 1.34-2 3-2s3 .9 3 2-1.34 2-3 2-3 .9-3 2 1.34 2 3 2 3-.9 3-2" />
                    </svg>
               );
          case "Tax savings":
               return (
                    <svg {...props}>
                         <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                         <path d="M16 3v4M8 3v4M2 11h20" />
                    </svg>
               );
          case "Toll perk":
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="10" />
                         <path d="M8 12h8M12 8v8" />
                    </svg>
               );
          case "Financing":
               return (
                    <svg {...props}>
                         <path d="M2 17l10-10 4 4 6-6" />
                         <path d="M14 7h8v8" />
                    </svg>
               );
          case "Discount":
               return (
                    <svg {...props}>
                         <line x1="19" y1="5" x2="5" y2="19" />
                         <circle cx="6.5" cy="6.5" r="2.5" />
                         <circle cx="17.5" cy="17.5" r="2.5" />
                    </svg>
               );
          case "Utility plan":
               return (
                    <svg {...props}>
                         <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
                    </svg>
               );
          case "Bill credit":
               return (
                    <svg {...props}>
                         <rect x="2" y="4" width="20" height="16" rx="2" />
                         <path d="M2 10h20" />
                    </svg>
               );
          default:
               return (
                    <svg {...props}>
                         <circle cx="12" cy="12" r="10" />
                         <path d="M12 16v-4M12 8h.01" />
                    </svg>
               );
     }
};

const sortIncentivesByValue = (incentives) => {
     const list = Array.isArray(incentives)
          ? incentives.filter((incentive) => incentive?.evaluation?.eligibility !== "ineligible")
          : [];

     return [...list].sort((a, b) => {
          const aAmount = parseIncentiveAmount(a);
          const bAmount = parseIncentiveAmount(b);

          if (aAmount !== bAmount) {
               return bAmount - aAmount;
          }

          const rankDifference = getTypeRank(a?.type) - getTypeRank(b?.type);

          if (rankDifference !== 0) {
               return rankDifference;
          }

          return String(a?.name || "").localeCompare(String(b?.name || ""));
     });
};

const useCountUp = (targetNumber) => {
     const [displayValue, setDisplayValue] = useState(0);

     useEffect(() => {
          const finalValue = Number.isFinite(targetNumber) ? Math.max(0, targetNumber) : 0;
          const animationDurationMs = 900;
          const animationStart = performance.now();
          let rafId;

          const animate = (time) => {
               const elapsed = time - animationStart;
               const progress = Math.min(elapsed / animationDurationMs, 1);
               const easedProgress = 1 - Math.pow(1 - progress, 3);
               setDisplayValue(Math.round(finalValue * easedProgress));
               if (progress < 1) {
                    rafId = requestAnimationFrame(animate);
               }
          };

          rafId = requestAnimationFrame(animate);
          return () => cancelAnimationFrame(rafId);
     }, [targetNumber]);

     return displayValue;
};

const FeaturedIncentiveCard = ({
     badgeText,
     badgeClassName,
     badgeIcon,
     stripeClassName,
     incentive,
     postcode,
     loading,
     lookupError,
     onZipSubmit,
     useLocale
}) => {
     if (!incentive) return null;

     const amount = parseIncentiveAmount(incentive);
     const amountText = amount > 0 ? FormatAsDollars(amount, useLocale) : null;
     const incentiveName = incentive?.name || "";
     const descriptionText = String(incentive?.description || "").trim();
     const sentenceBoundaryMatch = descriptionText.match(/^(.*?[.!?])\s+([A-Z].*)$/);
     const leadDescription =
          incentive?.lead_description || (sentenceBoundaryMatch ? sentenceBoundaryMatch[1] : descriptionText);
     const bodyDescription = incentive?.lead_description
          ? descriptionText
          : sentenceBoundaryMatch
            ? sentenceBoundaryMatch[2]
            : "";

     const defaultBadgeIcon = (
          <svg
               width="12"
               height="12"
               viewBox="0 0 24 24"
               fill="none"
               stroke="currentColor"
               strokeWidth="2.5"
               strokeLinecap="round"
               strokeLinejoin="round">
               <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
     );

     return (
          <div className="featured-spotlight-wrap" style={{ minHeight: 270 }}>
               <span className={`featured-top-badge ${badgeClassName || ""}`} aria-hidden="true">
                    {badgeIcon || defaultBadgeIcon}
                    {badgeText}
               </span>
               <div className="featured-card" style={{ minHeight: 270 }}>
                    <span className={`featured-stripe ${stripeClassName || ""}`} aria-hidden="true" />
                    <div className="featured-zip">
                         <ZipChip postcode={postcode} loading={loading} onZipChange={onZipSubmit} />
                    </div>
                    <div className="featured-left">
                         <span className="featured-pill">
                              {getPillIcon(getPillLabel(incentive?.type))}
                              <FormattedMessage
                                   id={PILL_LABEL_IDS[getPillLabel(incentive?.type)] || "incentives.pill.program"}
                                   defaultMessage={getPillLabel(incentive?.type)}
                              />
                         </span>
                         {amountText && (
                              <div className="featured-amount-wrap">
                                   <span className="featured-prefix">
                                        <FormattedMessage id="incentives.upTo" defaultMessage="Up to" />
                                   </span>
                                   <div className="featured-amount-row">
                                        <svg
                                             className="featured-bolt"
                                             width="28"
                                             height="28"
                                             viewBox="0 0 24 24"
                                             fill="#000"
                                             aria-hidden="true">
                                             <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
                                        </svg>
                                        <span className="featured-amount">{amountText}</span>
                                   </div>
                              </div>
                         )}
                         <h3 className={amountText ? "featured-title" : "featured-title featured-title--large"}>
                              {incentiveName}
                         </h3>
                    </div>
                    <div className="featured-right">
                         <p className="featured-desc">
                              {leadDescription && <strong className="featured-lead">{leadDescription}</strong>}
                              {bodyDescription && <span className="featured-desc-body">{bodyDescription}</span>}
                         </p>
                         <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                              <a
                                   href={incentive?.details_url || CARD_CTA_FALLBACK}
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   className="featured-card-learn-btn">
                                   <svg
                                        width="13"
                                        height="13"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.25"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true">
                                        <path d="M15 3h6v6" />
                                        <path d="M10 14 21 3" />
                                        <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                                   </svg>
                                   <FormattedMessage id="incentives.learnMore" defaultMessage="Learn more" />
                                   <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true">
                                        <path d="M5 12h14" />
                                        <path d="m12 5 7 7-7 7" />
                                   </svg>
                              </a>
                         </div>
                    </div>
               </div>
          </div>
     );
};

const HeroZipFormInline = ({ postcode, loading, lookupError, onSubmit }) => {
     const intl = useIntl();
     const [draftZipcode, setDraftZipcode] = useState(postcode || "");
     const [localError, setLocalError] = useState(null);
     const [touched, setTouched] = useState(false);

     useEffect(() => {
          setDraftZipcode(postcode || "");
     }, [postcode]);

     const validate = (value) => {
          const pathname = String(window.location?.pathname || "").toLowerCase();
          const isCanadian = pathname.includes("/ca");
          if (isCanadian) {
               if (!ValidateCAPostCode(value))
                    return intl.formatMessage({
                         id: "incentives.invalidPostalCode",
                         defaultMessage: "Enter a valid postal code."
                    });
               return null;
          }
          if (!ValidateFiveDigitCode(value))
               return intl.formatMessage({ id: "incentives.invalidZip", defaultMessage: "Enter a valid 5-digit ZIP." });
          return null;
     };

     const isInvalid = touched && (!!localError || !!lookupError);

     return (
          <div className="hero-zip-wrap">
               <form
                    className={`hero-zip-form${isInvalid ? " is-invalid" : ""}`}
                    noValidate
                    onSubmit={(event) => {
                         event.preventDefault();
                         if (loading) return;
                         setTouched(true);
                         const error = validate(draftZipcode);
                         if (error) {
                              setLocalError(error);
                              return;
                         }
                         setLocalError(null);
                         onSubmit(draftZipcode);
                    }}>
                    <label className="hero-zip-field" aria-label="ZIP code">
                         <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                              <circle cx="12" cy="10" r="3" />
                         </svg>
                         <input
                              type="text"
                              inputMode="numeric"
                              enterKeyHint="go"
                              autoComplete="off"
                              pattern="\d{5}"
                              maxLength={5}
                              placeholder={
                                   postcode ||
                                   intl.formatMessage({ id: "incentives.enterZip", defaultMessage: "Enter ZIP" })
                              }
                              value={draftZipcode}
                              aria-invalid={isInvalid}
                              onChange={(event) => {
                                   const nextValue = event.target.value.replace(/\D/g, "").slice(0, 5);
                                   setDraftZipcode(nextValue);
                                   if (localError) setLocalError(null);
                              }}
                              onFocus={() => {
                                   setDraftZipcode("");
                                   setLocalError(null);
                              }}
                              onBlur={() => {
                                   if (!draftZipcode) {
                                        setDraftZipcode(postcode || "");
                                        setTouched(false);
                                   }
                              }}
                         />
                    </label>
                    <button type="submit" className="hero-zip-btn" disabled={loading}>
                         {loading
                              ? intl.formatMessage({ id: "incentives.loadingZip", defaultMessage: "Loading…" })
                              : intl.formatMessage({
                                     id: "incentives.viewIncentives",
                                     defaultMessage: "View Incentives"
                                })}
                         {!loading && (
                              <svg
                                   width="16"
                                   height="16"
                                   viewBox="0 0 24 24"
                                   fill="none"
                                   stroke="currentColor"
                                   strokeWidth="2.5"
                                   strokeLinecap="round"
                                   strokeLinejoin="round"
                                   aria-hidden="true">
                                   <line x1="5" y1="12" x2="19" y2="12" />
                                   <polyline points="12 5 19 12 12 19" />
                              </svg>
                         )}
                    </button>
               </form>
               {isInvalid && (
                    <span className="hero-zip-error" role="alert">
                         <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                         </svg>
                         {localError ||
                              intl.formatMessage({
                                   id: "incentives.zipNotFound",
                                   defaultMessage: "We couldn’t find that ZIP code. Please try another."
                              })}
                    </span>
               )}
          </div>
     );
};

const Incentives = ({ electricVehicles, incentives: _incentivesProp, ip, uuid }) => {
     const userPrefs = useContext(UserPrefsContext);

     const postcode = userPrefs?.get ? userPrefs.get("zipcode") : "";
     const isZipUpdating = userPrefs?.zipcodeIsUpdating;
     const zipLookupError = userPrefs?.zipcodeIsNotFound;

     const [pageIncentives, setPageIncentives] = useState(_incentivesProp || null);

     useEffect(() => {
          if (!postcode) return;
          let cancelled = false;
          fetchPageIncentivesByZip(postcode)
               .then((data) => {
                    if (!cancelled && data) setPageIncentives(data);
               })
               .catch(() => {});
          return () => {
               cancelled = true;
          };
     }, [postcode]);

     const rawIncentives = pageIncentives ?? _incentivesProp;

     const incentives = useMemo(() => {
          return Array.isArray(rawIncentives)
               ? rawIncentives.filter((incentive) => !isExcludedPassengerEvIncentive(incentive))
               : rawIncentives;
     }, [rawIncentives]);

     const incentivesList = useMemo(() => sortIncentivesByValue(incentives), [incentives]);

     const topIncentive = HARD_CODED_TOP_INCENTIVE;
     const topIncentiveId = topIncentive?.id;

     const stateIncentiveCandidates = incentivesList.filter((incentive) => {
          if (!isEligibleIncentive(incentive)) return false;
          if (incentive?.id === topIncentiveId) return false;
          if (isUberGoElectricIncentive(incentive)) return false;
          if (!isStateLikeIncentive(incentive)) return false;

          const categories = categorizeIncentiveForHero(incentive);
          const isVehicle = categories.includes("new") || categories.includes("used");
          const isChargingOnly =
               categories.includes("charging") && !categories.includes("new") && !categories.includes("used");

          if (!isVehicle || isChargingOnly) return false;

          return true;
     });

     const stateIncentive = stateIncentiveCandidates[0] || null;

     const catalogIncentives = useMemo(() => {
          return Array.isArray(incentives) ? incentives : [];
     }, [incentives]);

     const computedHeroTotal = computeHeroTotal(incentives);
     const displayTotal = useCountUp(computedHeroTotal);

     useEffect(() => {
          document.title = process.env.REACT_APP_PAGES_INCENTIVES_TITLE;
     }, []);

     const intl = useIntl();

     const handleZipSubmit = async (zipcode) => {
          if (!userPrefs?.set || !userPrefs?.syncWorkingZipcode) return;

          await userPrefs.set({ workingZipcode: zipcode });
          userPrefs.syncWorkingZipcode();
     };

     return (
          <>
               <section className="incentives-page">
                    <div className="incentives-hero-bg">
                         <div className="incentives-hero-bg-container">
                              <div className="incentives-hero">
                                   <span className="incentives-eyebrow">
                                        <FormattedMessage id="incentives.eyebrow" defaultMessage="DRIVER SAVINGS" />
                                   </span>
                                   <h1 className="incentives-heading">
                                        <FormattedMessage
                                             id="incentives.heading"
                                             defaultMessage="You could be eligible for {amount} in EV savings"
                                             values={{
                                                  amount: (
                                                       <span className="incentives-heading-amount">
                                                            {FormatAsDollars(displayTotal, intl.locale)}
                                                       </span>
                                                  )
                                             }}
                                        />
                                   </h1>
                                   <p className="incentives-subtitle">
                                        <FormattedMessage
                                             id="incentives.subTitle"
                                             defaultMessage="These are federal, state, and local EV incentives you may qualify for based on your ZIP. You may qualify for these incentives based on income, tax filing, or vehicle choice — see the full list below."
                                             description="Incentives Subtitle"
                                        />
                                   </p>
                                   <div className="incentives-hero-actions">
                                        <HeroZipFormInline
                                             postcode={postcode || ""}
                                             loading={isZipUpdating}
                                             lookupError={zipLookupError}
                                             onSubmit={handleZipSubmit}
                                        />
                                   </div>
                              </div>
                         </div>
                         <svg
                              viewBox="0 0 1440 100"
                              preserveAspectRatio="none"
                              style={{
                                   position: "absolute",
                                   left: 0,
                                   right: 0,
                                   bottom: -1,
                                   width: "100%",
                                   height: 100,
                                   zIndex: 4,
                                   display: "block",
                                   pointerEvents: "none"
                              }}>
                              <defs>
                                   <linearGradient id="hero-curve-stroke" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#00838f" stopOpacity="0" />
                                        <stop offset="15%" stopColor="#00838f" stopOpacity="0.7" />
                                        <stop offset="50%" stopColor="#00838f" stopOpacity="1" />
                                        <stop offset="85%" stopColor="#00838f" stopOpacity="0.7" />
                                        <stop offset="100%" stopColor="#00838f" stopOpacity="0" />
                                   </linearGradient>
                              </defs>
                              <path d="M0,100 L0,95 Q720,45 1440,95 L1440,100 Z" fill="#ffffff" />
                              <path
                                   d="M0,95 Q720,45 1440,95"
                                   fill="none"
                                   stroke="url(#hero-curve-stroke)"
                                   strokeWidth="2"
                                   strokeLinecap="round"
                                   vectorEffect="non-scaling-stroke"
                              />
                         </svg>
                    </div>

                    <div className="incentives-main">
                         <FeaturedIncentiveCard
                              badgeText={intl.formatMessage({
                                   id: "incentives.topIncentiveBadge",
                                   defaultMessage: "Top incentive available"
                              })}
                              incentive={topIncentive}
                              postcode={postcode}
                              loading={isZipUpdating}
                              lookupError={zipLookupError}
                              onZipSubmit={handleZipSubmit}
                              useLocale={intl.locale}
                         />

                         <div style={{ height: 36 }} />

                         <FeaturedIncentiveCard
                              badgeText={intl.formatMessage({
                                   id: "incentives.topStateBadge",
                                   defaultMessage: "Top state incentive"
                              })}
                              badgeClassName="featured-top-badge--state"
                              badgeIcon={
                                   <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                        <polyline points="9 22 9 12 15 12 15 22" />
                                   </svg>
                              }
                              stripeClassName="featured-stripe--state"
                              incentive={stateIncentive}
                              postcode={postcode}
                              loading={isZipUpdating}
                              lookupError={zipLookupError}
                              onZipSubmit={handleZipSubmit}
                              useLocale={intl.locale}
                         />
                    </div>
                    <div id="incentives-catalog" className="incentives-catalog-wrap">
                         <div className="incentives-catalog">
                              <div className="incentives-catalog-header">
                                   <h2 className="incentives-catalog-title">
                                        <FormattedMessage
                                             id="incentives.title"
                                             defaultMessage="Electric vehicle incentives"
                                        />
                                   </h2>
                                   <p className="incentives-catalog-subtitle">
                                        <FormattedMessage
                                             id="incentives.catalogSubtitle"
                                             defaultMessage="You may be eligible for a range of incentives, including rebates, tax credits, and various other perks. Select Personalize incentives to learn more about which are available to you."
                                        />
                                   </p>
                                   <div className="incentives-catalog-actions">
                                        <button
                                             type="button"
                                             className="personalize-incentives-btn"
                                             onClick={() => userPrefs.toggleIncentivePrefsModal()}>
                                             <FormattedMessage
                                                  id="personalizeIncentives"
                                                  defaultMessage="Personalize incentives"
                                             />
                                        </button>
                                   </div>
                              </div>
                              <IncentiveCatalogCategories
                                   incentives={catalogIncentives}
                                   postcode={postcode}
                                   loading={isZipUpdating}
                                   onZipSubmit={handleZipSubmit}
                              />
                         </div>
                    </div>
                    <div style={{ display: "none" }} aria-hidden="true">
                         <IncentivePreferences electricVehicles={electricVehicles} />
                    </div>
               </section>
               <section className="incentives-cta-banner" aria-label="Apply for Uber Go Electric incentive">
                    <div className="incentives-cta-banner-inner">
                         <div className="incentives-cta-banner-text">
                              <span className="incentives-cta-banner-eyebrow">
                                   <FormattedMessage id="incentives.ctaEyebrow" defaultMessage="Driver Savings" />
                              </span>
                              <p className="incentives-cta-banner-heading">
                                   <FormattedMessage
                                        id="incentives.ctaHeading"
                                        defaultMessage="Get $4,000 for switching to an EV{lineBreak}through Uber’s Go Electric incentive"
                                        values={{ lineBreak: <br className="incentives-cta-banner-br" /> }}
                                   />
                              </p>
                         </div>
                         <a
                              href="https://www.uber.com/us/en/drive/services/electric/zero-emissions-incentive/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="incentives-cta-banner-btn">
                              <FormattedMessage id="incentives.ctaApply" defaultMessage="Apply now" />
                         </a>
                    </div>
               </section>
          </>
     );
};

export default Incentives;

Incentives.propTypes = {
     incentives: PropTypes.array,
     electricVehicles: PropTypes.array,
     zip: PropTypes.string,
     ip: PropTypes.string,
     uuid: PropTypes.string
};

FeaturedIncentiveCard.propTypes = {
     badgeText: PropTypes.string,
     badgeClassName: PropTypes.string,
     badgeIcon: PropTypes.node,
     stripeClassName: PropTypes.string,
     incentive: PropTypes.object,
     postcode: PropTypes.string,
     loading: PropTypes.bool,
     lookupError: PropTypes.bool,
     onZipSubmit: PropTypes.func,
     useLocale: PropTypes.string
};

HeroZipFormInline.propTypes = {
     postcode: PropTypes.string,
     loading: PropTypes.bool,
     lookupError: PropTypes.bool,
     onSubmit: PropTypes.func
};
